/**
 * Switching region (market) on the single www.vowsera.com domain.
 *
 * useSelectableMarkets — the regions a person may pick right now: those that
 *   are open, plus any still in Coming Soon for super-admins testing them.
 * useSwitchMarket — change region. For a signed-in account the change goes
 *   through PUT /auth/market first (refused while on a paid plan), so the
 *   account and the browser agree; then the page reloads in the new region.
 */
import { useCallback } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useMarket } from '@context/MarketContext';
import api from '@services/api';
import { MARKET, MARKET_KEYS, MARKET_SOURCE, chooseMarket } from '@utils/market';

export const useSelectableMarkets = () => {
  const { user } = useAuth();
  const { market } = useMarket();

  // A dedicated market domain (vowsera.in) is that market only.
  if (MARKET_SOURCE === 'domain') return [MARKET];

  const keys = new Set(['global', MARKET]);
  (market?.markets || []).forEach(m => {
    if (!m.comingSoon || user?.role === 'super_admin') keys.add(m.key);
  });
  return MARKET_KEYS.filter(k => keys.has(k));
};

export const useSwitchMarket = () => {
  const { user } = useAuth();

  return useCallback(async (key) => {
    if (!MARKET_KEYS.includes(key)) return { ok: false, message: 'Unknown region.' };
    if (key === MARKET) return { ok: true };

    if (user) {
      try {
        await api.put('/auth/market', { market: key });
      } catch (e) {
        return { ok: false, message: e?.response?.data?.message || 'Could not change your region. Please try again.' };
      }
    }

    chooseMarket(key);
    window.location.reload();
    return { ok: true };
  }, [user]);
};
